import React, { useState, useEffect } from 'react';
import { Calendar, Check, X, Users } from 'lucide-react';
import axios from 'axios';

const MarkAttendanceClasswise = () => {
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [filters, setFilters] = useState({ course_id: '', semester_id: '', section_id: '' });
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchMasterData();
  }, []);

  const fetchMasterData = async () => {
    try {
      const [coursesRes, semestersRes, sectionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/master-settings/courses`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/master-settings/semesters`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/master-settings/sections`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCourses(coursesRes.data);
      setSemesters(semestersRes.data);
      setSections(sectionsRes.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchStudents = async () => {
    if (!filters.course_id || !filters.semester_id) {
      alert('Please select course and semester');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.course_id) params.append('course_id', filters.course_id);
      if (filters.semester_id) params.append('semester_id', filters.semester_id);
      if (filters.section_id) params.append('section_id', filters.section_id);

      const response = await axios.get(`${API_URL}/api/students/?${params.toString()}&limit=1000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const studentsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setStudents(studentsData);

      const initialAttendance = {};
      studentsData.forEach(student => {
        initialAttendance[student.id] = 'Present';
      });
      setAttendance(initialAttendance);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAttendanceChange = (studentId, status) => {
    setAttendance(prev => ({ ...prev, [studentId]: status }));
  };

  const markAllPresent = () => {
    const newAttendance = {};
    students.forEach(student => {
      newAttendance[student.id] = 'Present';
    });
    setAttendance(newAttendance);
  };

  const markAllAbsent = () => {
    const newAttendance = {};
    students.forEach(student => {
      newAttendance[student.id] = 'Absent';
    });
    setAttendance(newAttendance);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const attendanceData = Object.entries(attendance).map(([studentId, status]) => ({
        student_id: studentId,
        date: selectedDate,
        status: status,
        marked_by: 'admin'
      }));

      await axios.post(
        `${API_URL}/api/attendance/mark-bulk`,
        { attendance: attendanceData },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Attendance marked successfully!');
    } catch (error) {
      console.error('Error marking attendance:', error);
      alert('Failed to mark attendance. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const presentCount = Object.values(attendance).filter(s => s === 'Present').length;
  const absentCount = Object.values(attendance).filter(s => s === 'Absent').length;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mark Attendance Class wise</h1>
          <p className="text-sm text-gray-500 mt-1">Mark attendance for entire class at once</p>
        </div>
        {students.length > 0 && (
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-blue-300"
          >
            {loading ? 'Saving...' : 'Save Attendance'}
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Select Class</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Course *</label>
            <select
              className="w-full px-3 py-2 border rounded-lg"
              value={filters.course_id}
              onChange={(e) => setFilters({ ...filters, course_id: e.target.value })}
            >
              <option value="">Select Course</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Semester *</label>
            <select
              className="w-full px-3 py-2 border rounded-lg"
              value={filters.semester_id}
              onChange={(e) => setFilters({ ...filters, semester_id: e.target.value })}
            >
              <option value="">Select Semester</option>
              {semesters.map(semester => (
                <option key={semester.id} value={semester.id}>{semester.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Section</label>
            <select
              className="w-full px-3 py-2 border rounded-lg"
              value={filters.section_id}
              onChange={(e) => setFilters({ ...filters, section_id: e.target.value })}
            >
              <option value="">All Sections</option>
              {sections.map(section => (
                <option key={section.id} value={section.id}>{section.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={fetchStudents}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Load Students
          </button>
        </div>
      </div>

      {students.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Students</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{students.length}</p>
                </div>
                <Users className="h-12 w-12 text-blue-500" />
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Present</p>
                  <p className="text-3xl font-bold text-green-600 mt-1">{presentCount}</p>
                </div>
                <Check className="h-12 w-12 text-green-500" />
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Absent</p>
                  <p className="text-3xl font-bold text-red-600 mt-1">{absentCount}</p>
                </div>
                <X className="h-12 w-12 text-red-500" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex gap-4">
              <button
                onClick={markAllPresent}
                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <Check className="h-4 w-4" />
                Mark All Present
              </button>
              <button
                onClick={markAllAbsent}
                className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
              >
                <X className="h-4 w-4" />
                Mark All Absent
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission No.</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {students.map((student) => (
                  <tr key={student.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{student.full_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{student.admission_number}</td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center gap-4">
                        <button
                          onClick={() => handleAttendanceChange(student.id, 'Present')}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                            attendance[student.id] === 'Present'
                              ? 'bg-green-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          <Check className="h-4 w-4" />
                          Present
                        </button>
                        <button
                          onClick={() => handleAttendanceChange(student.id, 'Absent')}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                            attendance[student.id] === 'Absent'
                              ? 'bg-red-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          <X className="h-4 w-4" />
                          Absent
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default MarkAttendanceClasswise;